import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { CabecalhoPagina } from '../components/layout/CabecalhoPagina';
import { SecaoFormulario, Dica } from '../components/common/Formulario';
import { athleteApi, beltApi, teamApi, userApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, CreditCard, UserCircle, Trophy, Plus, MapPin, Phone, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import SearchableCombobox from '../components/SearchableCombobox';
import { filterBeltsForBirthDate, beltAgeRangeText } from '../lib/beltRules';
// FASE E — documento em UM CAMPO SÓ (CPF ou RG), inteligente e opcional
import CampoDocumentoInteligente from '../components/CampoDocumentoInteligente';
import { comCamposDeDocumento } from '../lib/documentos';
// FASE 1 — nacionalidade (com sugestões de países) e naturalidade
import CampoPais from '../components/CampoPais';
import { normalizarNaturalidade } from '../lib/paises';
// FASE 2 — data da graduação ("faixa atual desde")
import CampoDataGraduacao from '../components/CampoDataGraduacao';

/** Section shown only for professors: edit their administrative profile
 *  (address + cell phone). Persists to `PATCH /api/users/{id}/profile`.
 *  Added in Wave 2. */
const ProfessorProfileCard = ({ user, onSaved }) => {
  const [data, setData] = useState({
    cell_phone: user?.cell_phone || '',
    country: user?.country || 'Brasil',
    state: user?.state || '',
    city: user?.city || '',
    neighborhood: user?.neighborhood || '',
    street_address: user?.street_address || '',
    street_number_complement: user?.street_number_complement || '',
    zip_code: user?.zip_code || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await userApi.updateProfile(user._id, data);
      toast.success('Perfil profissional atualizado');
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-[#141414] border-[#27272A]" data-testid="professor-profile-card">
      <CardContent className="p-6">
        <h2 className="text-xl font-bold text-white mb-1 font-['Chivo'] flex items-center gap-2"><MapPin size={16} className="text-[#D4AF37]" /> Dados Profissionais</h2>
        <Dica className="mb-4">Endereço e contato usados em ofícios da federação e na sua identidade institucional.</Dica>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="prof-cell" className="text-[#A1A1AA]">Celular</Label>
            <Input id="prof-cell" value={data.cell_phone} onChange={(e) => setData({ ...data, cell_phone: e.target.value })} placeholder="(21) 99999-9999" inputMode="tel" maxLength={20} data-testid="prof-cell" className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prof-cep" className="text-[#A1A1AA]">CEP</Label>
            <Input id="prof-cep" value={data.zip_code} onChange={(e) => setData({ ...data, zip_code: e.target.value })} placeholder="00000-000" inputMode="numeric" maxLength={9} data-testid="prof-cep" className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
          <div className="space-y-2">
            <Label htmlFor="prof-country" className="text-[#A1A1AA]">País</Label>
            <Input id="prof-country" value={data.country} onChange={(e) => setData({ ...data, country: e.target.value })} data-testid="prof-country" className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prof-state" className="text-[#A1A1AA]">Estado</Label>
            <Input id="prof-state" value={data.state} onChange={(e) => setData({ ...data, state: e.target.value })} placeholder="RJ" maxLength={2} data-testid="prof-state" className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prof-city" className="text-[#A1A1AA]">Cidade</Label>
            <Input id="prof-city" value={data.city} onChange={(e) => setData({ ...data, city: e.target.value })} data-testid="prof-city" className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div className="space-y-2">
            <Label htmlFor="prof-neighborhood" className="text-[#A1A1AA]">Bairro</Label>
            <Input id="prof-neighborhood" value={data.neighborhood} onChange={(e) => setData({ ...data, neighborhood: e.target.value })} data-testid="prof-neighborhood" className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prof-street" className="text-[#A1A1AA]">Endereço (Rua/Av)</Label>
            <Input id="prof-street" value={data.street_address} onChange={(e) => setData({ ...data, street_address: e.target.value })} data-testid="prof-street" className="bg-[#1A1A1A] border-[#27272A] text-white" />
          </div>
        </div>
        <div className="space-y-2 mt-3">
          <Label htmlFor="prof-number-complement" className="text-[#A1A1AA]">Nº / Complemento</Label>
          <Input id="prof-number-complement" value={data.street_number_complement} onChange={(e) => setData({ ...data, street_number_complement: e.target.value })} placeholder="123 — Apto 5" data-testid="prof-number-complement" className="bg-[#1A1A1A] border-[#27272A] text-white" />
        </div>
        <div className="flex justify-end mt-5">
          <Button onClick={handleSave} disabled={saving} data-testid="save-prof-profile" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Dados Profissionais'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function MyProfilePage() {
  const { user } = useAuth();
  const isProfessor = user?.role === 'professor';
  const [athlete, setAthlete] = useState(null);
  const [fullUser, setFullUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [belts, setBelts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    birth_date: '',
    // FASE E — documento em UM CAMPO SÓ (OPCIONAL): CPF ou RG
    documento: '',
    documento_tipo: 'auto',
    // FASE 1 — nacionalidade e naturalidade (opcionais; não nascem preenchidos)
    nationality: '',
    birth_place: '',
    // FASE 2 — data da graduação (opcional): desde quando tem a faixa atual
    belt_since: '',
    sex: 'M',
    weight: '',
    belt: '',
    team_id: user?.team_id || '',
    professor_id: '',
    federation_ids: [],
    phone: '',
    email: user?.email || ''
  });
  const [professors, setProfessors] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, beltsRes, teamsRes, profsRes] = await Promise.all([
          athleteApi.myProfile(),
          beltApi.list(),
          teamApi.list().catch(() => ({ data: [] })),
          userApi.list({ role: 'professor' }).catch(() => ({ data: [] })),
        ]);
        setAthlete(profileRes.data);
        setBelts(beltsRes.data);
        setTeams(teamsRes.data || []);
        setProfessors(profsRes.data || []);
        if (beltsRes.data.length > 0 && !formData.belt) {
          setFormData(prev => ({ ...prev, belt: beltsRes.data[0].name }));
        }
      } catch (e) { /* no profile */ }

      // For professors: also fetch their extended user profile (address/contact)
      if (isProfessor && user?._id) {
        try {
          const res = await userApi.list({ role: 'professor' });
          const me = (res.data || []).find(u => u._id === user._id);
          if (me) setFullUser(me);
        } catch (e) { /* ignore */ }
      }

      setLoading(false);
    };
    fetchProfile();
  }, []);

  // Filter professors by selected team: only show members/president of the
  // chosen team. Falls back to all professors if no team is selected.
  const professorOptions = useMemo(() => {
    const baseOpts = professors
      .filter((p) => p._id !== user?._id) // can't be your own supervisor
      .map((p) => ({ value: p._id, label: p.name, sublabel: p.email }));
    if (!formData.team_id) return baseOpts;
    const team = teams.find((t) => t._id === formData.team_id);
    if (!team) return baseOpts;
    const allowed = new Set([
      ...(team.professor_ids || []),
      ...(team.president_id ? [team.president_id] : []),
    ]);
    const filtered = baseOpts.filter((o) => allowed.has(o.value));
    return filtered.length ? filtered : baseOpts; // graceful fallback
  }, [professors, teams, formData.team_id, user?._id]);

  const teamOptions = useMemo(
    () => teams.map((t) => ({ value: t._id, label: t.name })),
    [teams],
  );

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if ((isProfessor || user?.role === 'athlete') && !formData.team_id) {
      toast.error('Selecione a Equipe antes de criar seu perfil');
      return;
    }
    setSaving(true);
    try {
      const data = comCamposDeDocumento({ ...formData, weight: parseFloat(formData.weight) || 0 });
      const { data: newProfile } = await athleteApi.createMyProfile(data);
      setAthlete(newProfile);
      setShowForm(false);
      if (newProfile?.status === 'pending') {
        toast.success('Perfil enviado para aprovação do seu professor!');
      } else if (isProfessor && formData.team_id) {
        const team = teams.find((t) => t._id === formData.team_id);
        toast.success(`Perfil criado! Você foi vinculado à equipe ${team?.name || ''}.`);
      } else {
        toast.success('Perfil de atleta criado com sucesso!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar perfil');
    } finally {
      setSaving(false);
    }
  };

  const getBeltStyle = (beltName) => {
    const b = belts.find(b => b.name === beltName);
    if (!b) return {};
    const isLight = ['#FFFFFF', '#EAB308', '#9CA3AF'].includes(b.color_code);
    return {
      backgroundColor: b.color_code,
      color: isLight ? '#000' : '#fff',
      border: b.color_code === '#000000' ? '1px solid #555' : b.color_code === '#FFFFFF' ? '1px solid #999' : 'none'
    };
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div></MainLayout>;
  }

  // No athlete profile — show creation form
  if (!athlete) {
    return (
      <MainLayout>
        <div className="space-y-6 max-w-2xl mx-auto" data-testid="my-profile-page">
          <CabecalhoPagina
            contexto="Cadastros"
            titulo="Meu Perfil de Atleta"
            icone={UserCircle}
          />

          {!showForm ? (
            <Card className="bg-[#141414] border-[#27272A]">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <UserCircle className="w-20 h-20 text-[#71717A] mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Perfil nao encontrado</h2>
                <p className="text-[#A1A1AA] text-center mb-1">
                  {isProfessor
                    ? 'Crie seu perfil de atleta para poder se inscrever nos campeonatos.'
                    : 'Crie seu perfil de atleta. Ele ficará pendente até a aprovação do seu professor.'}
                </p>
                <Button
                  onClick={() => setShowForm(true)}
                  className="mt-4 bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
                  data-testid="create-athlete-profile-btn"
                >
                  <Plus size={20} className="mr-2" /> Criar Meu Perfil de Atleta
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#141414] border-[#27272A]">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-white mb-6 font-['Chivo']">Criar Perfil de Atleta</h2>
                <form onSubmit={handleCreateProfile} className="space-y-6">
                  <SecaoFormulario titulo="Identificação" icone={UserCircle}>
                  <div className="space-y-2">
                    <Label htmlFor="profile-name" className="text-[#A1A1AA]">Nome Completo *</Label>
                    <Input
                      id="profile-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                      placeholder="SEU NOME COMPLETO"
                      required
                      data-testid="profile-name"
                      className="bg-[#1A1A1A] border-[#27272A] text-white uppercase"
                    />
                  </div>

                  {/* Data de Nascimento e o documento ficam LADO A LADO. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <div className="space-y-2">
                      <Label htmlFor="profile-birth-date" className="text-[#A1A1AA]">Data de Nascimento *</Label>
                      <Input
                        id="profile-birth-date"
                        type="date"
                        value={formData.birth_date}
                        onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                        required
                        data-testid="profile-birth-date"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      />
                    </div>
                    <div className="space-y-2" data-testid="profile-documento-block">
                      <CampoDocumentoInteligente
                        label="CPF ou RG"
                        value={formData.documento}
                        tipo={formData.documento_tipo}
                        onChange={(v) => setFormData({ ...formData, documento: v })}
                        onTipoChange={(t) => setFormData({ ...formData, documento_tipo: t })}
                        testId="profile-documento"
                        hint="Opcional. Digite o CPF ou o RG — o campo se vira sozinho."
                      />
                    </div>
                  </div>

                  {/* FASE 1 — Nacionalidade e Naturalidade na mesma linha. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <CampoPais
                      value={formData.nationality}
                      onChange={(v) => setFormData({ ...formData, nationality: v })}
                      testId="profile-nationality"
                      hint="Opcional. Comece a digitar para ver as sugestões."
                    />
                    <div className="space-y-2">
                      <Label htmlFor="profile-birth-place" className="text-[#A1A1AA]">Naturalidade</Label>
                      <Input
                        id="profile-birth-place"
                        value={formData.birth_place}
                        onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                        onBlur={(e) => setFormData(prev => ({ ...prev, birth_place: normalizarNaturalidade(e.target.value) }))}
                        placeholder="Ex: Rio de Janeiro/RJ"
                        data-testid="profile-birth-place"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      />
                      <Dica icone={false}>
                        Opcional. Cidade e estado onde nasceu (como na ficha).
                      </Dica>
                    </div>
                  </div>
                  </SecaoFormulario>

                  <SecaoFormulario titulo="Categoria e competição" icone={Trophy}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="profile-sex" className="text-[#A1A1AA]">Sexo *</Label>
                      <Select value={formData.sex} onValueChange={(v) => setFormData({ ...formData, sex: v })}>
                        <SelectTrigger id="profile-sex" data-testid="profile-sex" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="M" className="text-white">Masculino</SelectItem>
                          <SelectItem value="F" className="text-white">Feminino</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-weight" className="text-[#A1A1AA]">Peso (kg) *</Label>
                      <Input
                        id="profile-weight"
                        type="number"
                        step="0.1"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        placeholder="70.5"
                        required
                        data-testid="profile-weight"
                        className="bg-[#1A1A1A] border-[#27272A] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-belt" className="text-[#A1A1AA]">Faixa *</Label>
                      <Select value={formData.belt || "none"} onValueChange={(v) => setFormData({ ...formData, belt: v === "none" ? "" : v })}>
                        <SelectTrigger id="profile-belt" data-testid="profile-belt" className="bg-[#1A1A1A] border-[#27272A] text-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                          {filterBeltsForBirthDate(belts, formData.birth_date).map((b) => (
                            <SelectItem key={b._id} value={b.name} className="text-white hover:bg-[#27272A]">
                              <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: b.color_code, border: b.color_code === '#000000' ? '1px solid #555' : b.color_code === '#FFFFFF' ? '1px solid #999' : 'none' }} />
                                {b.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.belt && beltAgeRangeText(formData.belt) && (
                        <Dica icone={false}>
                          {beltAgeRangeText(formData.belt)} (regra CBJJ/IBJJF)
                        </Dica>
                      )}
                    </div>
                  </div>

                  {/* FASE 2 — desde quando tem a faixa atual (opcional). */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                    <CampoDataGraduacao
                      value={formData.belt_since}
                      onChange={(v) => setFormData({ ...formData, belt_since: v })}
                      belt={formData.belt}
                      startDate={formData.start_date}
                      birthDate={formData.birth_date}
                      testId="profile-belt-since"
                    />
                  </div>

                  </SecaoFormulario>

                  <SecaoFormulario titulo="Contato" icone={Phone}>
                  <div className="space-y-2">
                    <Label htmlFor="profile-phone" className="text-[#A1A1AA]">Telefone</Label>
                    <Input
                      id="profile-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      inputMode="tel"
                      maxLength={20}
                      data-testid="profile-phone"
                      className="bg-[#1A1A1A] border-[#27272A] text-white"
                    />
                  </div>
                  </SecaoFormulario>

                  {/* Wave 6 — Team & supervisor binding (Professor + Athlete self-registration) */}
                  {(isProfessor || user?.role === 'athlete') && (
                    <SecaoFormulario
                      titulo="Equipe e vínculo"
                      icone={UsersIcon}
                      dica={
                        isProfessor
                          ? 'Como professor, escolha sua Equipe e seu Supervisor para ser automaticamente vinculado.'
                          : 'Escolha sua Equipe e seu Professor. Seu cadastro ficará pendente até a aprovação dele.'
                      }
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">Equipe *</Label>
                          <SearchableCombobox
                            testId="profile-team-combobox"
                            value={formData.team_id || ''}
                            onChange={(v) => setFormData({ ...formData, team_id: v, professor_id: '' })}
                            options={teamOptions}
                            placeholder="Selecione a equipe"
                            searchPlaceholder="Digite para buscar..."
                            emptyText="Nenhuma equipe encontrada"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">
                            {isProfessor ? 'Professor supervisor' : 'Professor'}
                          </Label>
                          <SearchableCombobox
                            testId="profile-supervisor-combobox"
                            value={formData.professor_id || ''}
                            onChange={(v) => setFormData({ ...formData, professor_id: v })}
                            options={professorOptions}
                            placeholder={formData.team_id ? (isProfessor ? 'Selecione o supervisor' : 'Selecione o professor') : 'Selecione uma equipe primeiro'}
                            searchPlaceholder="Digite para buscar..."
                            emptyText="Nenhum professor encontrado"
                            disabled={!formData.team_id}
                            allowClear
                          />
                          <Dica icone={false}>
                            {isProfessor
                              ? 'Opcional — deixe em branco se você é independente.'
                              : 'Opcional — caso não selecione, apenas o Presidente da Equipe poderá aprovar.'}
                          </Dica>
                        </div>
                      </div>
                    </SecaoFormulario>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForm(false)}
                      className="border-[#27272A] text-[#A1A1AA]"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      data-testid="save-profile-btn"
                      className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar Perfil'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Professor extended profile (Wave 2) */}
          {isProfessor && fullUser && (
            <ProfessorProfileCard user={fullUser} />
          )}
        </div>
      </MainLayout>
    );
  }

  // Has athlete profile — show it
  return (
    <MainLayout>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="my-profile-page">
        <CabecalhoPagina
          contexto="Cadastros"
          titulo="Meu Perfil de Atleta"
          icone={UserCircle}
        />

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-6">
            <div className="flex items-center gap-6 mb-6">
              {athlete.photo_path ? (
                <img src={athlete.photo_path} alt={athlete.name} className="w-24 h-24 rounded-xl object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-[#1A1A1A] flex items-center justify-center text-[#D4AF37] text-3xl font-bold">
                  {athlete.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-white">{athlete.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 rounded text-xs font-bold" style={getBeltStyle(athlete.belt)}>
                    {athlete.belt}
                  </span>
                  <span className="text-[#A1A1AA] text-sm">{athlete.age_class} - {athlete.weight} kg</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="space-y-1"><span className="text-[#71717A]">Sexo</span><p className="text-white">{athlete.sex === 'M' ? 'Masculino' : 'Feminino'}</p></div>
              <div className="space-y-1"><span className="text-[#71717A]">Nascimento</span><p className="text-white">{athlete.birth_date}</p></div>
              <div className="space-y-1"><span className="text-[#71717A]">Documento</span><p className="text-white">{athlete.document}</p></div>
              <div className="space-y-1"><span className="text-[#71717A]">Email</span><p className="text-white">{athlete.email || '-'}</p></div>
              <div className="space-y-1"><span className="text-[#71717A]">Telefone</span><p className="text-white">{athlete.phone || '-'}</p></div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Link to={`/athletes/${athlete._id}/card`} className="flex-1">
            <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-3" data-testid="view-card-btn">
              <CreditCard size={20} className="mr-2" /> Ver Carteirinha
            </Button>
          </Link>
          {isProfessor && (
            <Link to="/championships" className="flex-1">
              <Button variant="outline" className="w-full border-[#27272A] text-white hover:bg-[#1A1A1A] font-bold py-3" data-testid="go-championships-btn">
                <Trophy size={20} className="mr-2" /> Ver Campeonatos
              </Button>
            </Link>
          )}
        </div>

        {/* Professor extended profile (Wave 2) */}
        {isProfessor && fullUser && (
          <ProfessorProfileCard user={fullUser} />
        )}
      </div>
    </MainLayout>
  );
}
